import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../components/layout/MainLayout";
import Breadcrumbs from "../components/common/Breadcrumbs";
import Swal from "sweetalert2";
import { selectHasPermission } from "../store/slices/authSlice";
import ConsentList from "../pages/Consent/ConsentList";
import { getApiErrorMessage } from "../utils/apiError";
import {
    fetchConsentForms,
    deleteConsentForm,
    clearError,
} from "../store/slices/consentSlice";

const Consents = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // ✅ Mock permissions
    const canManageConsents = useSelector((state) =>
        selectHasPermission(state, "consents-manage")
    );
    const canCreateConsents = useSelector((state) =>
        selectHasPermission(state, "consents-create")
    );
    const canEditConsents = useSelector((state) =>
        selectHasPermission(state, "consents-edit")
    );
    const canDeleteConsents = useSelector((state) =>
        selectHasPermission(state, "consents-delete")
    );

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // ✅ Simulate fetch
    useEffect(() => {
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), 800);
    }, []);

    // ✅ Action Handlers
    const handleAddConsent = () => navigate("/consent/add");
    const handleEditConsent = (id) => navigate(`/consent/edit/${id}`);
    const handleDeleteConsent = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You want to delete this consent form?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteConsentForm(id)).unwrap(); // call Redux delete action
                dispatch(fetchConsentForms()); // refresh list
                Swal.fire("Deleted!", "Consent has been deleted.", "success");
            } catch (err) {
                Swal.fire("Error!", getApiErrorMessage(err, "Failed to delete consent."), "error");
            }
        }
    };

    // ✅ Handle no permission (AFTER hooks)
    if (!canManageConsents) {
        return (
            <Navigate
                to="/dashboard"
                state={{ error: "You do not have permission to manage consents." }}
                replace
            />
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />

                {/* ===== Consent Table ===== */}
                <div className="card">
                    <div className="card-body">
                        {isLoading ? (
                            <div className="d-flex justify-content-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <ConsentList
                                onEditConsent={canEditConsents ? handleEditConsent : null}
                                onDeleteConsent={canDeleteConsents ? handleDeleteConsent : null}
                                onAddConsent={canCreateConsents ? handleAddConsent : null}
                            />
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default Consents;
